import { z } from 'zod';
import { ConditionDef } from '../transformations/condition';

export enum CarrierVerificationIntegrationType {
  HIGHWAY = 'HIGHWAY',
  HIGHWAY_EMAIL = 'HIGHWAY_EMAIL',
  HIGHWAY_PHONE = 'HIGHWAY_PHONE',
  HOSTED_MCLEOD = 'HOSTED_MCLEOD',
  LANDSTAR = 'LANDSTAR',
  MONTWAY = 'MONTWAY',
  MYCARRIERPORTAL = 'MYCARRIERPORTAL',
  TURVO = 'TURVO',
  RMIS = 'RMIS',
}

export const CarrierVerificationIntegrationTypeSchema = z.nativeEnum(
  CarrierVerificationIntegrationType
);

export enum CarrierVerificationAggregationMode {
  // NOTE(farzaan): ANY = pass if any integration passes (fail only if ALL fail)
  ANY = 'ANY',
  // NOTE(farzaan): ALL = pass only if all integrations pass (fail if ANY fails)
  ALL = 'ALL',
}

export const CarrierVerificationAggregationModeSchema = z.nativeEnum(
  CarrierVerificationAggregationMode
);

export const CarrierVerificationPolicySchema = z.object({
  failConditions: z.array(z.any()),
  failRiskConditions: z.array(z.any()),
  name: z.string().optional(),
  integrationType: CarrierVerificationIntegrationTypeSchema,
  // NOTE(bryn): Deprecated; kept so existing descriptions still parse. The editors no longer set it.
  description: z.string().optional(),
});
export type CarrierVerificationPolicy = z.infer<
  typeof CarrierVerificationPolicySchema
>;

export const CarrierVerificationNoIntegrationsResultSchema = z.enum([
  'FAIL',
  'MAYBE_PASS',
  'PASS',
]);
export type CarrierVerificationNoIntegrationsResult = z.infer<
  typeof CarrierVerificationNoIntegrationsResultSchema
>;

const CarrierVerificationIntegrationPolicyFieldsSchema = z.object({
  ignored: z.boolean().optional(),
  name: z.string().optional(),
  // NOTE(bryn): Deprecated; kept so existing descriptions still parse. The editors no longer set it.
  description: z.string().optional(),
  failConditions: z.array(z.any()).optional(),
  failRiskConditions: z.array(z.any()).optional(),
});
export type CarrierVerificationIntegrationPolicy = z.infer<
  typeof CarrierVerificationIntegrationPolicyFieldsSchema
>;

export const CarrierVerificationIntegrationPolicySchema =
  CarrierVerificationIntegrationPolicyFieldsSchema.passthrough().transform(
    (policy): CarrierVerificationIntegrationPolicy =>
      policy as CarrierVerificationIntegrationPolicy
  );

export const CarrierVerificationPoliciesV2Schema = z.object({
  version: z.literal(2),
  aggregationMode: CarrierVerificationAggregationModeSchema,
  noIntegrationsResult:
    CarrierVerificationNoIntegrationsResultSchema.optional(),
  integrations: z.record(
    CarrierVerificationIntegrationTypeSchema,
    CarrierVerificationIntegrationPolicySchema
  ),
});
export type CarrierVerificationPoliciesV2 = z.infer<
  typeof CarrierVerificationPoliciesV2Schema
>;

export function isCarrierVerificationPoliciesV2(
  value: unknown
): value is CarrierVerificationPoliciesV2 {
  return CarrierVerificationPoliciesV2Schema.safeParse(value).success;
}

export const CarrierVerificationResult = {
  PASS: 'PASS',
  MAYBE_PASS: 'MAYBE_PASS',
  FAIL: 'FAIL',
} as const;

export type CarrierVerificationResult =
  (typeof CarrierVerificationResult)[keyof typeof CarrierVerificationResult];

export const DEFAULT_CARRIER_VERIFICATION_AGGREGATION_MODE =
  CarrierVerificationAggregationMode.ALL;
export const DEFAULT_CARRIER_VERIFICATION_NO_INTEGRATIONS_RESULT =
  CarrierVerificationResult.FAIL;

export const CARRIER_NOT_FOUND_RISK_DESCRIPTION =
  'Carrier not found in verification system';

export type CarrierVerificationEvaluationResult = {
  result: CarrierVerificationResult;
  carrierName: string;
  matchedFailConditions: ConditionDef[];
  matchedRiskConditions: ConditionDef[];
  carrierNotFound?: boolean;
};

// NOTE(bryn): Latest verification result cached on a carrier so downstream
// readers (e.g. the coverage summary email) can render an approved/rejected
// badge without re-running the external integrations on their hot path.
export type CarrierVerificationSnapshot = {
  result: CarrierVerificationResult;
  summary?: string;
  failReasons: string[];
  verifiedAt: string;
};
